import React, { useState, useEffect } from 'react'
import {
    Box,
    Checkbox,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core'
import { Pagination } from '@material-ui/lab'
import { Description } from '@material-ui/icons'

import { DocumentViewer } from '../ResidentTermsTab/DocumentViewer'
import { DocumentProcessingStatus, LeaseDocument } from '../../types'
import { useDocumentsTableViewStyles } from './styles'

interface DocumentsTableViewProps {
    documents: LeaseDocument[]
    totalCount: number
    page: number
    pageSize: number
    isLoading: boolean
    onPageChange: (page: number) => void
    onPageSizeChange: (size: number) => void
    selectedDocumentIds: number[]
    onSelectionChange: (documentIds: number[]) => void
}

const formatProcessingStatus = (status?: DocumentProcessingStatus | null) => {
    switch (status) {
        case 'CREATED':
            return 'Created'
        case 'PARSING_PENDING':
            return 'Parsing Pending'
        case 'PARSING_IN_PROGRESS':
            return 'Parsing In Progress'
        case 'PARSED':
            return 'Parsed'
        case 'PARSING_FAILED':
            return 'Parsing Failed'
        case 'SELECTION_PENDING':
            return 'Selection Pending'
        case 'SELECTION_COMPLETE':
            return 'Selection Complete'
        case 'SELECTION_FAILED':
            return 'Selection Failed'
        case 'EXTRACTION_PENDING':
            return 'Extraction Pending'
        case 'EXTRACTION_IN_PROGRESS':
            return 'Extraction In Progress'
        case 'EXTRACTED':
            return 'Extracted'
        case 'EXTRACTION_FAILED':
            return 'Extraction Failed'
    }
    return 'Unknown'
}

export const DocumentsTableView: React.FC<DocumentsTableViewProps> = ({
    documents,
    totalCount,
    page,
    pageSize,
    isLoading,
    onPageChange,
    onPageSizeChange,
    selectedDocumentIds,
    onSelectionChange,
}) => {
    const classes = useDocumentsTableViewStyles()
    const [viewingDocument, setViewingDocument] = useState<{
        id: number
        name: string
        file?: string
    } | null>(null)
    const [viewportHeight, setViewportHeight] = useState<number>(
        typeof window !== 'undefined' ? window.innerHeight : 0,
    )

    const totalPages = Math.ceil(totalCount / pageSize)
    const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
    const rangeEnd =
        totalCount === 0
            ? 0
            : Math.min((page - 1) * pageSize + documents.length, totalCount)

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(value)
    }

    const handlePageSizeChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        onPageSizeChange(Number(event.target.value))
    }

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            onSelectionChange(documents.map((doc) => doc.id))
        } else {
            onSelectionChange([])
        }
    }

    const handleToggleSelection = (documentId: number) => {
        onSelectionChange(
            selectedDocumentIds.includes(documentId)
                ? selectedDocumentIds.filter((id) => id !== documentId)
                : [...selectedDocumentIds, documentId],
        )
    }

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight)
        }

        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    return (
        <Box className={classes.container}>
            {isLoading ? (
                <Box className={classes.emptyState}>
                    <CircularProgress />
                </Box>
            ) : documents.length === 0 ? (
                <Box className={classes.emptyState}>
                    <Typography variant="h6" gutterBottom>
                        No documents found
                    </Typography>
                    <Typography variant="body2">
                        Try adjusting your filters or search term.
                    </Typography>
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    className={classes.tableContainer}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={
                                            documents.length > 0 &&
                                            selectedDocumentIds.length ===
                                                documents.length
                                        }
                                        indeterminate={
                                            selectedDocumentIds.length > 0 &&
                                            selectedDocumentIds.length <
                                                documents.length
                                        }
                                        onChange={(event) =>
                                            handleSelectAll(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                </TableCell>
                                <TableCell>Resident Term</TableCell>
                                <TableCell>Document Title</TableCell>
                                <TableCell>Paired with Resident Term</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">View</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documents.map((doc) => (
                                <TableRow
                                    key={doc.id}
                                    className={
                                        doc.is_selected
                                            ? classes.selectedRow
                                            : classes.unselectedRow
                                    }
                                    hover
                                >
                                    {/* checkbox */}
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedDocumentIds.includes(
                                                doc.id,
                                            )}
                                            onChange={() =>
                                                handleToggleSelection(doc.id)
                                            }
                                        />
                                    </TableCell>
                                    {/* resident term */}
                                    <TableCell>
                                        <Typography variant="subtitle2">
                                            {doc.resident}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {doc.lease_start} - {doc.lease_end}
                                        </Typography>
                                    </TableCell>
                                    {/* document title */}
                                    <TableCell>
                                        <Typography variant="subtitle2">
                                            {doc.file_name || doc.title}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            Last Modified:{' '}
                                            {new Date(
                                                doc.modified_date,
                                            ).toLocaleDateString()}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        {doc.resident_term ? 'Yes' : 'No'}
                                    </TableCell>
                                    <TableCell>
                                        <span className={classes.statusBadge}>
                                            {formatProcessingStatus(
                                                doc.processing_status,
                                            )}
                                        </span>
                                    </TableCell>

                                    {/* View document */}
                                    <TableCell align="right">
                                        <Tooltip title="View document">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setViewingDocument({
                                                        id: doc.id,
                                                        name: doc.file_name,
                                                        file: doc.file,
                                                    })
                                                }
                                            >
                                                <Description fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {totalPages > 1 && (
                <Box className={classes.pagination}>
                    <TextField
                        select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        variant="outlined"
                        size="small"
                        style={{ width: 80 }}
                    >
                        {[25, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                    <Typography variant="body2" color="textSecondary">
                        {rangeStart} - {rangeEnd} of {totalCount}
                    </Typography>
                </Box>
            )}

            <DocumentViewer
                open={!!viewingDocument}
                documentId={viewingDocument?.id || null}
                documentName={viewingDocument?.name}
                documentUrl={viewingDocument?.file}
                onClose={() => setViewingDocument(null)}
            />
        </Box>
    )
}
